import { useEffect, useRef, useState } from "react";

/** Bounded decoded-frame cache. Superseded requests never become the visible frame. */
export function useBitmapPreview(url: string | undefined, enabled: boolean, nearby: string[] = []) {
  const cache = useRef(new Map<string, ImageBitmap>());
  const [, redraw] = useState(0);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const nearbyKey = nearby.join("\n");
  useEffect(() => {
    if (!enabled || !url) return;
    const controller = new AbortController();
    const load = async (target: string) => {
      const cached = cache.current.get(target);
      if (cached) { cache.current.delete(target); cache.current.set(target, cached); return; }
      const response = await fetch(target, {signal: controller.signal, credentials: "same-origin"});
      if (!response.ok) throw new Error("Frame preview unavailable");
      const bitmap = await createImageBitmap(await response.blob());
      if (controller.signal.aborted) { bitmap.close(); return; }
      cache.current.set(target, bitmap);
      while (cache.current.size > 12) {
        const oldest = cache.current.keys().next().value!;
        cache.current.get(oldest)?.close(); cache.current.delete(oldest);
      }
    };
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          await load(url);
          if (controller.signal.aborted) return;
          setFailedUrl(null); redraw(n => n + 1);
        } catch { if (!controller.signal.aborted) setFailedUrl(url); return; }
        // Adjacent frames are cheap to revisit, but never compete with the selected frame.
        for (const neighbour of nearbyKey.split("\n").filter(Boolean)) {
          if (controller.signal.aborted) break;
          try { await load(neighbour); } catch { /* Prefetch is optional. */ }
        }
      })();
    }, 20);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [url, enabled, nearbyKey]);
  useEffect(() => () => {
    for (const bitmap of cache.current.values()) bitmap.close();
    cache.current.clear();
  }, []);
  return {bitmap: enabled && url ? cache.current.get(url) : undefined, failed: failedUrl === url};
}
